import React, { useState, useEffect } from 'react';
import ActivityItem from './ActivityItem';
import { Loader, ChevronLeft, ChevronRight } from 'lucide-react';

const ProfileActivityFeed = ({ userId, showPagination = true }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const API_BASE = process.env.REACT_APP_API_BASE || '/api';
  const itemsPerPage = 5;

  useEffect(() => {
    const fetchActivities = async () => {
      if (!userId) return;
      
      setLoading(true);
      setError('');

      try {
        const offset = (currentPage - 1) * itemsPerPage;
        const res = await fetch(`${API_BASE}/users/${userId}/activity/combined?limit=${itemsPerPage}&offset=${offset}`, {
          credentials: 'include',
        });

        if (res.ok) {
          const data = await res.json();
          setActivities(data.activities || []);
          setHasMore(data.hasMore || false);
          setTotal(data.total || 0);
        } else {
          setError('Failed to load activity');
        }
      } catch (err) {
        console.error('Error fetching activity:', err);
        setError('Failed to load activity');
      } finally {
        setLoading(false);
      }
    };

    fetchActivities();
  }, [userId, currentPage, itemsPerPage, API_BASE]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="animate-spin text-cyan-400" size={32} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center">
        <p className="text-red-400">{error}</p>
      </div>
    );
  }

  if (activities.length === 0) {
    return (
      <div className="bg-white/[0.04] border border-white/10 rounded-xl p-12 text-center">
        <div className="text-white/40 text-lg mb-2">No activity yet</div>
        <p className="text-white/30 text-sm">
          Activity from forum posts, comments, and ArtHouse interactions will appear here
        </p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / itemsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 7;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const handlePageClick = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        {activities.map((activity, index) => (
          <ActivityItem key={`${activity.type}-${activity.id}-${index}`} activity={activity} />
        ))}
      </div>

      {/* Pagination Controls */}
      {showPagination && totalPages > 1 && (
        <div className="mt-6 pt-6 border-t border-white/10 space-y-3">
          {/* Page numbers */}
          <div className="flex items-center justify-center flex-wrap gap-2">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all border ${
                currentPage === 1
                  ? 'bg-black/20 border-white/5 text-white/30 cursor-not-allowed'
                  : 'bg-black/20 border-white/10 hover:bg-cyan-500/20 hover:border-cyan-400/30 text-white'
              }`}
            >
              <ChevronLeft size={16} />
            </button>

            {getPageNumbers().map((page, idx) => (
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="px-2 text-white/40">...</span>
              ) : (
                <button
                  key={page}
                  onClick={() => handlePageClick(page)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all min-w-[40px] border ${
                    currentPage === page
                      ? 'bg-cyan-500/20 border-cyan-400/40 text-cyan-200'
                      : 'bg-black/20 border-white/10 hover:bg-white/10 text-white'
                  }`}
                >
                  {page}
                </button>
              )
            ))}

            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-all border ${
                currentPage === totalPages
                  ? 'bg-black/20 border-white/5 text-white/30 cursor-not-allowed'
                  : 'bg-black/20 border-white/10 hover:bg-cyan-500/20 hover:border-cyan-400/30 text-white'
              }`}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Page info */}
          <div className="text-center">
            <span className="text-white/55 text-sm">
              Page {currentPage} of {totalPages} • {total} total
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileActivityFeed;
